import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import {
  TablePagination,
  Grid,
  IconButton,
  Typography,
  Switch,
  Tooltip,
  useTheme,
} from "@material-ui/core";
import {
  Edit,
  Message,
} from "@material-ui/icons";
import { defaultComponents } from '../../../controls/Material-Table-Components';

import {
  MaskedText,
  MaskedTime,
  ComboSelect,
  LightTooltip,
  HtmlTooltip,
} from "../../../controls";
import Icons from "../../../icons";
import SlotHistoryDialog from "../../../controls/FormDialog";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { getSeason } from "../../../functions/GetSeason";
import SlotsValidator from "../validators/SlotsValidator";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";

import { useSlotDates } from "../../../services/slots";
import { useSeasons } from "../../../services/settings";
import Season from "./Season";
import GenerateSlotDialog from "./GenerateSlotDialog";
import SendSlotRequestDialog from "./SendSlotRequestDialog";
import MessageExchangeGrid from "./SlotMessageExchangeGrid";
import UpdateSlotDialog from "./UpdateSlotDialog";
import { Stack } from '@mui/material';
import TrafficLight from '../../../controls/TrafficLight';


const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const statusValues = [
  {"code":"REQUIRED","value":"Required"},
  {"code":"REQUESTED","value":"Requested"},
  {"code":"RECEIVED","value":"Confirmed"},
  {"code":"REJECTED","value":"Rejected"},
  {"code":"CANCELLED","value":"Cancelled"},
  {"code":"PENDING","value":"Pending"},
  {"code":"OFFER","value":"Offer"},
  {"code":"CONDITIONAL","value":"Conditional"},
];

const serviceValues = [
  {"code":"F","value":"Scheduled"},
  {"code":"H","value":"Charter"},
  {"code":"P","value":"Ferry"},
  {"code":"X","value":"Tech"},
];

const historyButtons = {
  cancel:{icon: "checkCircle", label: "OK", color: "primary"}
};


const SlotListExpanded = (_props) => {
  const props = {
    ..._props
  }
  const {classes, eventBus} = props;
  const theme = useTheme();

  const {isAuthenticated} = useAuth0();

  const [state] = useState({
    canEdit: props.canEdit,
    canManuallyUpdateSlots: props.canManuallyUpdateSlots,
  });
  const [generateSlot, setGenerateSlot] = useState({
    open: false,
    record: {}
  });
  const [sendSlot, setSendSlot] = useState({
    open: false,
    record: {},
    resend: true
  });
  const [slotMessageExchange, setSlotMessageExchange] = useState({
    showNoteDialog: false,
    record: null,
  });
  const [updateSlot, setUpdateSlot] = useState({
    open: false,
    record: {},
    resend: true
  });

  const toggleExplodedView = () => {
    eventBus.trigger('SlotManagement.toggleExplodedView');
  }

  const toggleCancelled = () => {
    eventBus.trigger('SlotManagement.toggleCancelled');
  }

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required)
    extraKeys.push(true);

  const slotDates = useSlotDates({tableRef, queryClient, extraKeys});
  const seasons = useSeasons({ });


  const refreshList = async () => {
    queryClient.invalidateQueries(["SlotDates", year, month]);
  }

  const handleUpdateSlotClick = (rowData) => {
    const season = getSeason(seasons.data, rowData.slotPeriodFrom);
    const slotActionCode = "";
    const newSlotMode = false;
    const record = {
      ...__.pick(rowData, [
        'slotType', 
        'slotDirection',
        'slotReference',
        'companyUuid',
        'airportCode',
        'slotActionCode',
        'slotLevel',
        'arrivalAirlineCode',
        'arrivalFlightNo',
        'departureAirlineCode',
        'departureFlightNo',
        'slotPeriodFrom',
        'slotPeriodTo',
        'days',  
        'aircraftTailNumber',
        'arrivalService',
        'departureService',
        'frequency',
        'status', 
        'offsetDays'
      ]),
      originAirportCode: rowData.slotOriginAirportCode,
      previousAirportCode: rowData.slotDestinationAirportCode,
      arrivalTime: rowData.arrivalFlightTime,
      departureTime: rowData.departureFlightTime,
      destinationAirportCode: rowData.slotDestinationAirportCode,
      newSlotMode,
      slotActionCode,
      season: season,
      generalInfo: "REGARDS MAGMA AVIATION",
      previousOffsetDays: rowData.offsetDays
    }
    record['parentRecord'] = {...record};
    setGenerateSlot({...generateSlot, open: true, newSlot: false, record});
  }

  const getColumns = (props) => {
    const validatorColumn = ValidatorColumn(SlotsValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const baseColumnGrey = {
      ...baseColumn,
      cellStyle: { fontSize:12, padding: "4px 2px", backgroundColor: "#e0e0e0" },      
    }

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Direction",
        field: "recordType",
        sorting: true,
        render: (rowData) => (
          <LightTooltip title={rowData.recordType === 'INBOUND'?'Inbound':'Outbound'}>
            <div>
            {rowData.recordType === 'INBOUND'?
              <Icons>AirplaneLandingIcon</Icons>
              :
              <Icons>AirplaneTakeoffIcon</Icons>
              }
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Airport",
        field: "slotAirportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.slotAirportCode} />}>
            <div>
            {rowData.slotAirportCode}
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(95, 8)),
        title: "Flight No.",
        field: "airlineCode",
        initialEditValue: "",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.airlineCode} ${rowData.flightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.airlineCode}-${rowData.flightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.airlineCode}${rowData.flightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        render: (props) => (
          <div>
            {props.airlineCode}&nbsp;{props.flightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Flight Date",
        field: "flightDate",
        render: rowData => (
          <div>{moment.utc(rowData.flightDate).format('DD MMM YYYY')}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(90)),
        title: "Flight Time",
        field: "flightTime",
        render: rowData => (
          <div>{rowData.flightTime.substr(0,5)}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(90)),
        title: "Day",
        field: "flightDate",
        render: rowData => (
          <div>{moment.utc(rowData.flightDate).format('dddd')}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Status",
        field: "status",
        customFilterAndSearch: (term, rowData) => {
          let status = 'green';
          if (!rowData.tripLegUuid) {
            status = 'red';
          } else {
            const origin = rowData.recordType==='INBOUND'?rowData.slotOriginAirportCode:rowData.slotAirportCode;
            const destination = rowData.recordType==='OUTBOUND'?rowData.slotDestinationAirportCode:rowData.slotAirportCode;
            if (origin !== rowData.legOrigin) {
              status = 'amber'
            }
            if (destination !== rowData.legDestination) {
              status = 'amber'
            }
            const legTime = rowData.recordType==='INBOUND'?moment.utc(rowData.legArrivalTime).format('HH:mm'):moment.utc(rowData.legDepartureTime).format('HH:mm');
            if (rowData.flightTime.substring(0,5) !== legTime) {
              status = 'amber'
            }
          }
          if (`${term}`.toLowerCase() === 'error' && status !== 'green') return true;
          return status === `${term}`.toLowerCase();
        },
        render: rowData => {
          let status = 'green';
          let statustext = 'Flight found';
          if (!rowData.tripLegUuid) {
            status = 'red';
            statustext = 'No matching flight found';
          } else {
            let amberStatus = [];
            const origin = rowData.recordType==='INBOUND'?rowData.slotOriginAirportCode:rowData.slotAirportCode;
            const destination = rowData.recordType==='OUTBOUND'?rowData.slotDestinationAirportCode:rowData.slotAirportCode;
            if (origin !== rowData.legOrigin) {
              status = 'amber'
              amberStatus.push(`Origin Airport (${origin})) does not match slot origin (${rowData.legOrigin})`);
            }
            if (destination !== rowData.legDestination) {
              status = 'amber'
              amberStatus.push(`Destination Airport (${destination}) does not match slot destination (${rowData.legDestination})`);
            }
            const legTime = rowData.recordType==='INBOUND'?moment.utc(rowData.legArrivalTime).format('HH:mm'):moment.utc(rowData.legDepartureTime).format('HH:mm');
            if (rowData.flightTime.substring(0,5) !== legTime) {
              status = 'amber'
              amberStatus.push('Flight time does not match slot time');
            }
            if (amberStatus.length) {
              statustext = amberStatus.join(', ');
            }
          }
          return (
            <div style={{marginTop: 3}}>
              <Tooltip rowData={rowData} title={ statustext } >
                <div>
                  <TrafficLight
                    height={18}
                    width={50}
                    lights={3}
                    backgroundStyle={{fill: "#f0f0f0"}}
                    offStyle={{fill: "#e0e0e0"}}
                    status={status}
                  />
                </div>
              </Tooltip>
            </div>
          );
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(125)),
        title: "Pending Action",
        field: "status",
        editable: "never",
        cellStyle: (value, rowData) => {
          const bgcolor = ['REQUIRED', 'REJECTED', 'OFFER', ].includes(value)? "red" : ['REQUESTED', 'CONDITIONAL', 'PENDING', ].includes(value) ? "orange" : "";
          const color = ['REQUIRED', 'REJECTED', 'OFFER', ].includes(value)? "white" : ['REQUESTED', 'CONDITIONAL', 'PENDING', ].includes(value) ? "black" : "";
          const {cellStyle} = ColumnWidth(100);
          return ({...cellStyle, backgroundColor: bgcolor, color, textAlign: "center" });
        },
        render: (rowData) => {
          console.log(rowData);
          return (
          <>
          {rowData.status !== 'RECEIVED'?
            <div>
              <ComboSelect
                id="slotActionCode"
                label="Slot Request"
                group="SLOTS"
                subGroup="SHORT ACTIONS"
                fullWidth
                value={`${rowData.slotActionCode}`}
                displayOnly={true}
                style={{display: "inline-block"}}
              />
              <div style={{display: "inline-block", width: 8}}>-</div>
              <div style={{display: "inline-block"}}>{ __.find(statusValues, {code: rowData.status||"REQUESTED"}).value}</div>
            </div>
            :null}
          </>
        )},
      },
      {
        ...(ColumnWidth(5,)),
        ...baseColumnGrey,
      },
      {
        ...(ColumnWidth(10,)),
        ...baseColumn,
      },
      {
        ...baseColumn,
        ...(ColumnWidth(150)),
        title: "Route",
        field: "slotAirportCode",
        initialEditValue: "",
        sorting: true,
        searchable: false,
        editable: state.canEdit ? "always" : "never",
        render: (rowData) => (
          <Stack direction="row" spacing={0} alignItems="center">
            <LightTooltip title={<AirportNameTooltip airportCode={rowData.slotOriginAirportCode} />}>
              <span> {rowData.slotOriginAirportCode} </span>
            </LightTooltip>
            <Icons style={{rotate: "90deg", fontSize: 12, padding: 4}}>flight</Icons>
            <LightTooltip title={<AirportNameTooltip airportCode={rowData.slotAirportCode} />}>
              <span> {rowData.slotAirportCode} </span>
            </LightTooltip>
            <Icons style={{rotate: "90deg", fontSize: 12, padding: 4}}>flight</Icons>
            <LightTooltip title={<AirportNameTooltip airportCode={rowData.slotDestinationAirportCode} />}>
              <span> {rowData.slotDestinationAirportCode} </span>
            </LightTooltip>
          </Stack>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 8)),
        title: "Inbound Flight",
        field: "arrivalAirlineCode",
        initialEditValue: "",
        sorting: true,
        searchable: false,
        render: (props) => (
          <div>
            {props.arrivalAirlineCode}&nbsp;{props.arrivalFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Arrival Time",
        field: "arrivalFlightTime",
        searchable: false,
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.arrivalFlightTime?.substr(0,Math.min(rowData.arrivalFlightTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 8)),
        title: "Outbound Flight",
        field: "departureAirlineCode",
        initialEditValue: "",
        sorting: true,
        searchable: false,
        render: (props) => (
          <div>
            {props.departureAirlineCode}&nbsp;{props.departureFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Departure Time",
        field: "departureFlightTime",
        searchable: false,
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.departureFlightTime?.substr(0,Math.min(rowData.departureFlightTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period From",
        field: "slotPeriodFrom",
        searchable: false,
        render: rowData => {
          const displayDate = moment.utc(rowData.slotPeriodFrom).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period To",
        field: "slotPeriodTo",
        searchable: false,
        render: rowData => {
          const displayDate = moment.utc(rowData.slotPeriodTo).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(125)),
        title: "Season",
        field: "slotPeriodTo",
        searchable: false,
        editable: "never",
        render: rowData => <Season slotFrom={rowData.slotPeriodFrom} />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(5,)),
      },
      {
        ...(ColumnWidth(55)),
        ...baseColumn,
        title: "Days",
        field: "days",
        editable: false,
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"D".repeat(7)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(50, 2, 200)),
        title: "Service",
        field: "arrivalService",
        render: (rowData) => (
          <div>
            <HtmlTooltip title={ <span>{__.find(serviceValues, {code: rowData.arrivalService||"H"}).value} </span> } >
              <span> { rowData.arrivalService} </span>
            </HtmlTooltip>
            <HtmlTooltip title={ <span>{__.find(serviceValues, {code: rowData.departureService||"H"}).value} </span> } >
              <span> { rowData.departureService} </span>
            </HtmlTooltip>
          </div>
        ),
      },
      {
        ...baseColumn,
        title: "Reference",
        field: "slotReference",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData =>
          <span> {rowData.slotReference} </span>
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Requested On",
        field: "requestedTime",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.requestedTime).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.requestedTime ? moment.utc(rowData.requestedTime).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
    ];
  };


  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
//          rowStyle: (rowData)=> (rowData.recordStatus==='NEW'?{backgroundColor: "rgba(230,230,230,1)", opacity: 1}:{}),
          rowStyle: (rowData)=> (rowData.recordStatus==='CANCELLED'?{textDecoration: "line-through", opacity: .6}:{}),
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //overflowY: "scroll",
        }}
        components={{
          // ...defaultComponents,
          Toolbar: (toolbarProps) => {
            return (
              <div style={{ position: "relative" }}>
                <MTableToolbar {...toolbarProps} />
                {props.canToggleExplodedView?(
                  <div style={{position: "absolute", left: 200, top: 16}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Expand</Typography>
                      <Switch checked={true} onChange={toggleExplodedView}/>
                    </Stack>                    
                  </div>
                ): (<></>)}
                <div style={{position: "absolute", left: 350, top: 16}}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Hide Cancelled</Typography>
                    <Switch checked={props.hideCancelled} onChange={toggleCancelled}/>
                  </Stack>                    
                </div>
              </div>
            );
          },
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          (rowData) => (
            {
              hidden: false,
              disabled: false,
              icon: () => (
                <HtmlTooltip
                  rowData={rowData}
                  title={
                    <React.Fragment>
                      <Typography color="inherit">Message Exchange</Typography>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {
                          rowData.messageExchange !== null
                            ?
                              rowData.messageExchange.map(exchange => {
                                const date = moment(exchange.messageDate, "YYYY=MM-DD").format("DD/MM/YYYY");
                                const display = `${date} - ${exchange.action}`;
                                return <div><span>{display}</span><br/></div>
                              })
                            : <></>
                        }
                      </div>
                    </React.Fragment>
                  }
                >
                  <IconButton
                    variant="contained" color="primary" size={"small"}
                  >
                    <Message/>
                  </IconButton>
                </HtmlTooltip>),
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              onClick: (e, rowData) => {
                if (rowData.messageExchange && Array.isArray(rowData.messageExchange) && rowData.messageExchange.length > 0)
                  setSlotMessageExchange({showNoteDialog: true, rowData: rowData});
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              icon: () => (
                <IconButton variant="contained" size={"small"} color={"secondary"} disabled={!state.canEdit || ['RECEIVED', 'REJECTED', 'CANCELLED'].indexOf(rowData.status) < 0}>
                  <Edit/>
                </IconButton>
              ),
              tooltip: "Update Slot",
              //iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              onClick: (e, rowData) => {
                if ( !state.canEdit || ['RECEIVED', 'REJECTED', 'CANCELLED'].indexOf(rowData.status) < 0)
                  return;
                handleUpdateSlotClick(rowData);
              }
            }
          ),
        ]}
        title="Slot Viewer"
        columns={getColumns({canEdit: state.canEdit})}
        data={props.hideCancelled?__.filter(slotDates.data, (record) => record.recordStatus !== 'CANCELLED'):slotDates.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined
        }}
      />

      {generateSlot.open ?
        <GenerateSlotDialog
          record={generateSlot.record}
          canEdit={state.canEdit}
          close={()=>{setGenerateSlot({open: false, record: {}})}}
          send={(slotRequest, slotRecord, originalRecord)=>{
            setSendSlot({open: true, request: slotRequest, record: slotRecord, originalRecord: originalRecord, resend: false});
            setGenerateSlot({open: false, record: {}});
          }}
          classes={classes}
          dialogStates={{showDialog: true}}
          newSlot={generateSlot.newSlot}
          seasons={seasons.data}
        />
        : <></>
      }
      {updateSlot.open ?
        <UpdateSlotDialog
          request={updateSlot.request}
          record={updateSlot.record}
          originalRecord={updateSlot.originalRecord}
          resend={updateSlot.resend}
          canEdit={state.canEdit}
          close={()=>{setUpdateSlot({open: false, record: {}, resend: true})}}
          classes={classes}
          dialogStates={{showDialog: true, airport: updateSlot.record?.airport}}
        />
        : <></>
      }

      {sendSlot.open ?
        <SendSlotRequestDialog
          request={sendSlot.request}
          record={sendSlot.record}
          originalRecord={sendSlot.originalRecord}
          resend={sendSlot.resend}
          canEdit={state.canEdit}
          close={()=>{setSendSlot({open: false, record: {}, resend: true})}}
          classes={classes}
          dialogStates={{showDialog: true, airport: sendSlot.record?.airport}}
        />
        : <></>
      }

      {slotMessageExchange.showNoteDialog
        ?
        <SlotHistoryDialog
          title="Slot Message Exchange"
          message=""
          open={slotMessageExchange.showNoteDialog}
          record={{notes: slotMessageExchange.rowData.messageExchange}}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          buttons={historyButtons}
          onChange={(result) => {
            setSlotMessageExchange({...slotMessageExchange, showNoteDialog: false, rowData: null});
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true}>
              <MessageExchangeGrid messages={slotMessageExchange.rowData.messageExchange}/>
            </Grid>
          </Grid>
        </SlotHistoryDialog>
        :
        <></>
      }

    </div>
  );


}

export default SlotListExpanded;
